import { css } from '@emotion/react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, ConfirmationModal, InlineNotification, ModalControlModalProps, Text } from '@frontend/design-system';
import { MAX_WIDTH } from './constants';

interface Props {
  isUsingOfficeHours: boolean;
  onToggleOverride: () => void;
}

export const OfficeHoursOverrideBanner = ({ isUsingOfficeHours, onToggleOverride }: Props) => {
  const { t } = useTranslation('schedule');

  const { title, message, buttonLabel } = isUsingOfficeHours
    ? {
        title: t('Using Location Appointment Hours'),
        message: t(
          "This provider's office hours match the hours set at the associated location. To make changes specific to this provider, you can customize their hours."
        ),
        buttonLabel: t('Customize  Hours'),
      }
    : {
        title: t('Using Custom Provider Hours'),
        message: t('This provider is using customized hours that override the location default'),
        buttonLabel: t('Revert to Location Hours'),
      };

  return (
    <AnimatePresence>
      <InfoNotification
        key={title}
        title={title}
        message={message}
        buttonLabel={buttonLabel}
        onButtonClick={onToggleOverride}
      />
    </AnimatePresence>
  );
};

type OfficeHoursOverrideConfirmationProps = ModalControlModalProps & {
  onConfirm: () => void | Promise<void>;
};

export const OfficeHoursOverrideConfirmation = ({ onConfirm, ...modalProps }: OfficeHoursOverrideConfirmationProps) => {
  const { t } = useTranslation('schedule');
  return (
    <ConfirmationModal
      {...modalProps}
      title={t('Are you sure?')}
      message={t("This will override the provider office hours with this location's appointment hours.")}
      confirmLabel={t('Confirm')}
      cancelLabel={t('Cancel')}
      onConfirm={onConfirm}
      onCancel={modalProps.onClose}
    />
  );
};

interface InfoNotificationProps {
  title: string;
  message: string;
  buttonLabel?: string;
  onButtonClick?: () => void;
}

const InfoNotification = ({ title, message, buttonLabel, onButtonClick }: InfoNotificationProps) => {
  return (
    <AnimatePresence>
      <motion.div
        key={title}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <InlineNotification
          status='info'
          css={{ margin: theme.spacing(1, 0), maxHeight: 'none', maxWidth: MAX_WIDTH, '> *': { flex: 1 } }}
        >
          <div css={styles.contentContainer}>
            <div css={{ flex: 1, minWidth: 'fit-content' }}>
              <Text size='medium' weight='bold' css={{ marginBottom: theme.spacing(0.5) }}>
                {title}
              </Text>
              <Text size='small'>{message}</Text>
            </div>
            <Button variant='tertiary' onClick={onButtonClick} css={styles.button}>
              {buttonLabel}
            </Button>
          </div>
        </InlineNotification>
      </motion.div>
    </AnimatePresence>
  );
};

const styles = {
  contentContainer: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    column-gap: ${theme.spacing(2)};
    flex: 1;
  `,
  button: css`
    padding-left: 0;
  `,
};
